import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { NuqsAdapter } from "nuqs/adapters/react-router/v6"
import { BrowserRouter } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { getErrorMessage, isErrorToastHandled } from "@/api/instance"
import { AppRoutes } from "@/app/routes"
import { Toaster } from "@/components/ui/sonner"
import { customErrorMap } from "@/lib/zod-error-map"

// Zodのエラーメッセージを日本語化
z.setErrorMap(customErrorMap)

const showApiErrorToast = (error: unknown) => {
  if (isErrorToastHandled(error)) return
  toast.error("エラーが発生しました。", { description: getErrorMessage(error) })
}

const queryClient = new QueryClient({
  // 画面が onError を書いていれば、その画面が表示を担当する
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return
      showApiErrorToast(error)
    },
  }),
  // リトライし終えてから 1 回だけ出す。404 は画面側で扱う
  queryCache: new QueryCache({
    onError: (error) => {
      if ((error as { status?: number }).status === 404) return
      showApiErrorToast(error)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: (failureCount, error) => {
        // 401/403エラーの場合はリトライしない
        if (error instanceof Error) {
          interface ErrorWithStatus extends Error {
            status?: number
          }
          const errorData = error as ErrorWithStatus
          if (errorData.status === 401 || errorData.status === 403) {
            return false
          }
        }
        // それ以外は1回までリトライ
        return failureCount < 1
      },
      throwOnError: false,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NuqsAdapter>
          <AppRoutes />
          <Toaster />
        </NuqsAdapter>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
